/**
 * Robust decimal number parser that handles both US and European formats
 * 
 * Formats handled:
 * - US: 1,000.50 (comma = thousands, dot = decimal)
 * - European: 1.000,50 (dot = thousands, comma = decimal)
 * - Ambiguous: 0,0025 or 1,25 (treated as European decimal)
 * - Simple: 25 or 0.005 (no separators or single dot)
 */
export function parseDecimal(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0;
  }

  // If already a number, return it
  if (typeof value === 'number') {
    return value;
  }

  // Convert to string and trim
  let cleaned = value.toString().trim();
  
  if (cleaned === '' || cleaned === '-') {
    return 0;
  }

  // Handle negative sign
  const isNegative = cleaned.startsWith('-');
  if (isNegative) {
    cleaned = cleaned.substring(1);
  }

  // Find positions of last comma and last dot
  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  // Case 1: Both comma and dot present - last one is decimal separator
  if (lastComma > -1 && lastDot > -1) {
    if (lastComma > lastDot) {
      // European: 1.000,50 → remove dots, convert comma to dot
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else {
      // US: 1,000.50 → remove commas
      cleaned = cleaned.replace(/,/g, '');
    }
  }
  // Case 2: Only comma present
  else if (lastComma > -1) {
    const commaMatches = cleaned.match(/,/g) || [];
    const hasMultipleCommas = commaMatches.length > 1;
    const [intPart, fracPart = ''] = cleaned.split(',');

    // Treat as thousands style only when:
    // - multiple commas (e.g., 1,234,567), OR
    // - exactly three digits after comma AND integer part is not '0' (e.g., 25,000)
    const isThousandsStyle = hasMultipleCommas || (
      intPart !== '0' && /^\d+$/.test(intPart) && /^\d+$/.test(fracPart) && fracPart.length === 3
    );

    if (isThousandsStyle) {
      // Thousands separator: remove all commas
      cleaned = cleaned.replace(/,/g, '');
    } else {
      // European decimal: 0,0025 or 1,25 or 2,5 → replace the first comma with dot
      cleaned = `${intPart}.${fracPart}`;
    }
  }
  // Case 3: Only dot present
  else if (lastDot > -1) {
    // Check if it matches European thousands separator pattern (e.g., 1.000 or 10.000.000)
    if (/^\d{1,3}(\.\d{3})+$/.test(cleaned)) {
      // European thousands: remove dots
      cleaned = cleaned.replace(/\./g, '');
    }
    // Otherwise it's already in correct US decimal format (e.g., 0.005 or 12.34)
  }

  // Remove any remaining non-numeric characters except dots and minus
  cleaned = cleaned.replace(/[^0-9.]/g, '');

  const result = parseFloat(cleaned);
  return isNegative ? -result : result;
}

/**
 * Format a number for display, optionally with fixed decimal places
 */
export function formatDecimal(value: number, decimals?: number): string {
  if (decimals !== undefined) {
    return value.toFixed(decimals);
  }
  return value.toString();
}
